import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline, Edit, EditOff, Save } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useI18n } from '../i18n';
import { SETUP_FLOW_PATH } from '../nav/paths';
import { dashboardApi, shopfloorApi, signalsApi } from '../api';
import { DashboardTopicValue, WidgetCatalogItem } from '../types';
import { SignalsDefinition } from '../signals/types';
import {
  bindStacklightFromCatalog,
  resolveStacklightLive,
  stacklightCommandTarget,
  stacklightWatchTopics,
} from '../signals/stacklightBinding';
import SignalsCommandDialog from '../signals/components/SignalsCommandDialog';
import { useLiveMqttValues } from '../hooks/useLiveMqttValues';
import HallBoard, { HallSignalVisual } from './HallBoard';
import {
  HALL_HEIGHT_M,
  HALL_WIDTH_M,
  isShopfloorCatalogItem,
  normalizePlacedSignal,
  PlacedSignal,
  SHOPFLOOR_WIDGET_DND,
} from './types';

function readScalar(value: DashboardTopicValue | null | undefined): number | null {
  if (!value) return null;
  if (typeof value.value === 'number' && Number.isFinite(value.value)) return value.value;
  if (typeof value.temp === 'number' && Number.isFinite(value.temp)) return value.temp;
  if (typeof value.rh === 'number' && Number.isFinite(value.rh)) return value.rh;
  if (value.raw) {
    const n = Number(String(value.raw).trim());
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function readHumidity(value: DashboardTopicValue | null | undefined): number | null {
  if (!value) return null;
  if (typeof value.rh === 'number' && Number.isFinite(value.rh)) return value.rh;
  return readScalar(value);
}

function clampHall(x: number, y: number): { x: number; y: number } {
  return {
    x: Math.max(1, Math.min(HALL_WIDTH_M - 1, x)),
    y: Math.max(2, Math.min(HALL_HEIGHT_M - 1, y)),
  };
}

function placedFromCatalog(item: WidgetCatalogItem, x: number, y: number): PlacedSignal | null {
  const pos = clampHall(x, y);
  if (item.type === 'temperature') {
    const topic = String(item.topic || '').trim();
    if (!topic) return null;
    return {
      id: `t_${Date.now()}`,
      type: 'temperature',
      catalogId: item.id,
      topic,
      topicRh: item.topicRh,
      label: item.label || item.hostname,
      ...pos,
    };
  }
  if (item.type === 'sml_meter') {
    const topic = String(item.topic || '').trim();
    if (!topic) return null;
    return {
      id: `t_${Date.now()}`,
      type: 'sml_meter',
      catalogId: item.id,
      topic,
      topicPower: item.topicPower,
      topicReverse: item.topicReverse,
      label: item.label || item.hostname,
      ...pos,
    };
  }
  if (item.type === 'switch') {
    const topic = String(item.topic || '').trim();
    if (!topic) return null;
    return {
      id: `t_${Date.now()}`,
      type: 'switch',
      catalogId: item.id,
      topic,
      icon: item.icon === 'bulb' ? 'bulb' : 'switch',
      of: item.of,
      key: item.key,
      hostname: item.hostname,
      label: item.label || item.hostname,
      ...pos,
    };
  }
  if (item.type === 'stacklight') {
    const signalName = String(item.signalName || '').trim();
    if (!signalName) return null;
    return {
      id: `t_${Date.now()}`,
      type: 'stacklight',
      catalogId: item.id,
      signalName,
      stacklight: item.stacklight || undefined,
      hostname: item.hostname || undefined,
      topic: item.topic || undefined,
      label: item.label || item.hostname || signalName,
      ...pos,
    };
  }
  return null;
}

const ShopfloorPage: React.FC = () => {
  const { t } = useI18n();
  const [placed, setPlaced] = useState<PlacedSignal[]>([]);
  const [signalDefs, setSignalDefs] = useState<Record<string, SignalsDefinition>>({});
  const [catalog, setCatalog] = useState<WidgetCatalogItem[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [cmdId, setCmdId] = useState<string | null>(null);
  const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [widgets, layout] = await Promise.all([
          dashboardApi.getCatalog().catch(() => [] as WidgetCatalogItem[]),
          shopfloorApi.get(),
        ]);
        if (cancelled) return;
        const shopCatalog = widgets.filter(isShopfloorCatalogItem);
        const nextPlaced = (layout.signals || [])
          .map(normalizePlacedSignal)
          .filter((item): item is PlacedSignal => Boolean(item))
          .map((item) => (item.type === 'stacklight' ? bindStacklightFromCatalog(item, shopCatalog) : item));
        setCatalog(shopCatalog);
        setPlaced(nextPlaced);
        const names = new Set<string>();
        for (const widget of shopCatalog) {
          if (widget.type === 'stacklight' && widget.signalName) {
            names.add(widget.signalName);
          }
        }
        for (const item of nextPlaced) {
          if (item.type === 'stacklight' && item.signalName) {
            names.add(item.signalName);
          }
        }
        const defs: Record<string, SignalsDefinition> = {};
        await Promise.all(Array.from(names).map(async (name) => {
          try {
            defs[name] = await signalsApi.get(name);
          } catch {
            // ignore missing files
          }
        }));
        if (!cancelled) setSignalDefs(defs);
      } catch {
        // empty catalog if API unavailable
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const mqttTopics = useMemo(() => {
    const topics = new Set<string>();
    for (const item of placed) {
      if (item.type === 'temperature') {
        if (item.topic) topics.add(item.topic);
        if (item.topicRh) topics.add(item.topicRh);
        continue;
      }
      if (item.type === 'sml_meter') {
        if (item.topic) topics.add(item.topic);
        if (item.topicPower) topics.add(item.topicPower);
        if (item.topicReverse) topics.add(item.topicReverse);
        continue;
      }
      if (item.type === 'switch') {
        if (item.topic) topics.add(item.topic);
        continue;
      }
      const def = item.signalName ? signalDefs[item.signalName] : undefined;
      stacklightWatchTopics(item, def).forEach((topic) => topics.add(topic));
    }
    return Array.from(topics);
  }, [placed, signalDefs]);

  const { values, refresh: refreshValues } = useLiveMqttValues(mqttTopics);

  const signalVisuals: HallSignalVisual[] = useMemo(
    () => placed.map((item) => {
      if (item.type === 'temperature') {
        const tempValue = item.topic ? values[item.topic] : undefined;
        const rhValue = item.topicRh ? values[item.topicRh] : undefined;
        const temp = readScalar(tempValue);
        const rh = rhValue != null
          ? readHumidity(rhValue)
          : (typeof tempValue?.rh === 'number' && Number.isFinite(tempValue.rh) ? tempValue.rh : null);
        return { ...item, signals: [], temp, rh };
      }
      if (item.type === 'sml_meter') {
        const energy = item.topic ? readScalar(values[item.topic]) : null;
        const power = item.topicPower ? readScalar(values[item.topicPower]) : null;
        return { ...item, signals: [], energy, power };
      }
      if (item.type === 'switch') {
        const raw = item.topic ? values[item.topic] : undefined;
        let on: boolean | null = null;
        if (raw) {
          if (typeof raw.value === 'number' && Number.isFinite(raw.value)) {
            on = raw.value !== 0;
          } else {
            const text = String(raw.raw || raw.value || '').trim().toLowerCase();
            if (text === '1' || text === 'on' || text === 'true' || text === 'ein') on = true;
            else if (text === '0' || text === 'off' || text === 'false' || text === 'aus') on = false;
            else {
              const n = Number(text);
              if (Number.isFinite(n)) on = n !== 0;
            }
          }
        }
        return { ...item, signals: [], switchOn: on };
      }
      const def = item.signalName ? signalDefs[item.signalName] : undefined;
      return {
        ...item,
        signals: def?.signals || [],
        liveResolved: resolveStacklightLive(item, def, values),
      };
    }),
    [placed, signalDefs, values],
  );

  const selectedItem = useMemo(
    () => (selectedId ? placed.find((item) => item.id === selectedId) || null : null),
    [selectedId, placed],
  );
  const cmdItem = useMemo(
    () => (cmdId ? placed.find((item) => item.id === cmdId && item.type === 'stacklight') || null : null),
    [cmdId, placed],
  );
  const cmdDef = cmdItem?.signalName ? signalDefs[cmdItem.signalName] : null;
  const canCommand = selectedItem?.type === 'stacklight';

  const persist = async () => {
    await shopfloorApi.save({ signals: placed });
    setDirty(false);
    await refreshValues();
  };

  const onDropWidget = (item: WidgetCatalogItem, x: number, y: number) => {
    const next = placedFromCatalog(item, x, y);
    if (!next) return;
    setPlaced((prev) => [...prev, next]);
    setSelectedId(next.id);
    setDirty(true);
  };

  const placeFromMenu = (item: WidgetCatalogItem) => {
    onDropWidget(item, 20 + placed.length * 3, 12);
    setAddAnchor(null);
  };

  const removeSelected = () => {
    if (!selectedId) return;
    setPlaced((prev) => prev.filter((item) => item.id !== selectedId));
    setSelectedId(null);
    setDirty(true);
  };

  if (loading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <HallBoard
        signals={signalVisuals}
        selectedSignalId={selectedId}
        editMode={editMode}
        onSelectSignal={(item) => setSelectedId(item.id)}
        onActivateSignal={(item) => {
          if (item.type === 'stacklight') {
            setCmdId(item.id);
            return;
          }
          if (item.type === 'switch' && item.icon !== 'bulb' && item.topic) {
            const visual = signalVisuals.find((row) => row.id === item.id);
            const next = !(visual?.switchOn === true);
            const command = item.of && item.key
              ? dashboardApi.commandRelay({
                hostname: item.hostname || '',
                of: item.of,
                key: item.key,
                on: next,
              })
              : dashboardApi.publishMqtt(item.topic, next ? '1' : '0', { retain: true });
            void command
              .then(() => refreshValues())
              .catch((err) => console.error(err));
          }
        }}
        onMoveSignal={(id, x, y) => {
          setPlaced((prev) => prev.map((item) => (item.id === id ? { ...item, x, y } : item)));
          setDirty(true);
        }}
        onDropWidget={onDropWidget}
      />

      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          p: 0.5,
          borderRadius: 2,
          zIndex: 2,
        }}
      >
        {canCommand && (
          <Button size="small" onClick={() => setCmdId(selectedId)}>
            {t('dashboard.signalsCommands')}
          </Button>
        )}
        {editMode && (
          <>
            <Button size="small" onClick={(e) => setAddAnchor(e.currentTarget)}>
              {t('shopfloor.addSignals')}
            </Button>
            <Tooltip title={t('shopfloor.removeSignals')}>
              <span>
                <IconButton size="small" color="error" disabled={!selectedId} onClick={removeSelected}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('shopfloor.save')}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={!dirty}
                  onClick={() => { persist().catch(console.error); }}
                >
                  <Save fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        <Button
          size="small"
          variant={editMode ? 'contained' : 'outlined'}
          startIcon={editMode ? <EditOff /> : <Edit />}
          onClick={() => {
            if (editMode && dirty) {
              persist().catch(console.error);
            }
            setEditMode((v) => !v);
            setAddAnchor(null);
          }}
        >
          {editMode ? t('shopfloor.done') : t('shopfloor.edit')}
        </Button>
      </Paper>

      <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={() => setAddAnchor(null)}>
        {catalog.filter((item) => isShopfloorCatalogItem(item)).length === 0 ? (
          <MenuItem component={RouterLink} to={SETUP_FLOW_PATH} onClick={() => setAddAnchor(null)}>
            {t('shopfloor.noSignals')} {t('nav.flow')}
          </MenuItem>
        ) : (
          catalog.filter((item) => isShopfloorCatalogItem(item)).map((item) => (
            <MenuItem
              key={item.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'copy';
                e.dataTransfer.setData(SHOPFLOOR_WIDGET_DND, JSON.stringify(item));
              }}
              onClick={() => placeFromMenu(item)}
            >
              <Box>
                <Typography variant="body2">{item.label || item.hostname}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[
                    item.flowName ? t('dashboard.fromFlow', { name: item.flowName }) : '',
                    item.topic || item.signalName || t('shopfloor.dragOrClick'),
                  ].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>

      <SignalsCommandDialog
        open={Boolean(cmdItem)}
        onClose={() => setCmdId(null)}
        title={cmdItem?.label || cmdDef?.metadata?.name || cmdItem?.signalName || ''}
        definition={cmdDef}
        {...stacklightCommandTarget(cmdItem, cmdDef)}
        values={values}
        onPublished={refreshValues}
      />
    </Box>
  );
};

export default ShopfloorPage;
